import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, Component } from 'react';
import { listenSystemThemeChange } from './utill/utill';
import ProtectedRoute from './utill/ProtectedRoute';
import AdminRoute from './utill/AdminRoute';
import NotFound from './utill/NotFound';
import Login from './features/auth/Login';
import ForgotPassword from './features/auth/ForgotPassword';
import UserSignup from './features/auth/UserSignup';
import AdminLogin from './features/auth/AdminLogin';
import TermsOfService from './features/auth/TermsOfService';
import PrivacyPolicy from './features/auth/PrivacyPolicy';
import OAuthCallback from './features/auth/OAuthCallback';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh', gap: '16px',
          fontFamily: 'var(--font-sans, Pretendard, sans-serif)',
          color: 'var(--text-primary, #1a1a2e)',
          background: 'var(--background, #fff)',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--danger-500, #ef4444)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2 style={{ margin: 0, fontSize: '18px' }}>오류가 발생했습니다</h2>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary, #6b7280)' }}>
            페이지를 새로고침해 주세요.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', borderRadius: 'var(--radius-md, 6px)',
              border: 'none', background: 'var(--primary-600, #9333ea)',
              color: '#fff', fontSize: '14px', cursor: 'pointer',
            }}
          >
            새로고침
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// 비로그인 진입 페이지 (Landing)
const LandingHome = lazy(() => import('./features/landing/LandingHome'));
const LandingNotice = lazy(() => import('./features/landing/LandingNotice'));
const LandingPrice = lazy(() => import('./features/landing/LandingPrice'));
const LandingClasses = lazy(() => import('./features/landing/LandingClasses'));
const LandingSupport = lazy(() => import('./features/landing/LandingSupport'));

// 인증 후 라우트: 코드 스플리팅
const Home = lazy(() => import('./features/home/Home'));
const LlmChat = lazy(() => import('./features/llm-practice/LlmChat'));
const PromptIntro = lazy(() => import('./features/prompt/PromptIntro'));
const PromptGenerator = lazy(() => import('./features/prompt/PromptGenerator'));
const PromptList = lazy(() => import('./features/prompt/PromptList'));
const PromptEdit = lazy(() => import('./features/prompt/PromptEdit'));
const KnowledgeList = lazy(() => import('./features/knowledge/KnowledgeList'));
const McpIntro = lazy(() => import('./features/mcp/McpIntro'));
const McpList = lazy(() => import('./features/mcp/McpList'));
const McpGenerator = lazy(() => import('./features/mcp/McpGenerator'));
const McpBuilder = lazy(() => import('./features/mcp/McpBuilder'));
const AgentList = lazy(() => import('./features/agent/AgentList'));
const AgentCreate = lazy(() => import('./features/agent/AgentCreate'));
const AgentIntegrations = lazy(() => import('./features/agent/AgentIntegrations'));
const AgentSkills = lazy(() => import('./features/agent/AgentSkills'));
const AgentChat = lazy(() => import('./features/agent/AgentChat'));
const AgentShowcase = lazy(() => import('./features/agent/AgentShowcase'));
const AgentShowcaseDetail = lazy(() => import('./features/agent/AgentShowcaseDetail'));
const AgentShowcaseWork = lazy(() => import('./features/agent/AgentShowcaseWork'));
const WorkMockExample = lazy(() => import('./features/agent/WorkMockExample'));
const AgentSharePage = lazy(() => import('./features/agent/AgentSharePage'));
const ShinjinTeamAgent = lazy(() => import('./features/shinjin/ShinjinTeamAgent'));
const ShinjinDayDoc = lazy(() => import('./features/shinjin/ShinjinDayDoc'));
const ShinjinDayResult = lazy(() => import('./features/shinjin/ShinjinDayResult'));
const ShinjinLabShell = lazy(() => import('./features/shinjin/lab/LabShell'));
const EduList = lazy(() => import('./features/edu/EduList'));
const EduCourse = lazy(() => import('./features/edu/EduCourse'));
const EduCourseSteps = lazy(() => import('./features/edu/EduCourseSteps'));
const EduMy = lazy(() => import('./features/edu/EduMy'));
const EduEnrolled = lazy(() => import('./features/edu/EduEnrolled'));
const TemplateList = lazy(() => import('./features/template/TemplateList'));
const ProjectList = lazy(() => import('./features/project/ProjectList'));
const ProjectDetail = lazy(() => import('./features/project/ProjectDetail'));
const SettingsAccount = lazy(() => import('./features/settings/SettingsAccount'));
const SettingsSecurity = lazy(() => import('./features/settings/SettingsSecurity'));
const SettingsUsage = lazy(() => import('./features/settings/SettingsUsage'));
const SettingsBilling = lazy(() => import('./features/settings/SettingsBilling'));
const SettingsNotifications = lazy(() => import('./features/settings/SettingsNotifications'));
const SettingsPreferences = lazy(() => import('./features/settings/SettingsPreferences'));
const SettingsConnections = lazy(() => import('./features/settings/SettingsConnections'));
const SettingsSupport = lazy(() => import('./features/settings/SettingsSupport'));
const BillingSuccess = lazy(() => import('./features/billing/BillingSuccess'));
const BillingFail = lazy(() => import('./features/billing/BillingFail'));

const StationAgents = lazy(() => import('./features/station/StationAgents'));
const StationStatistics = lazy(() => import('./features/station/StationStatistics'));
const StationFavorites = lazy(() => import('./features/station/StationFavorites'));
const StationMessages = lazy(() => import('./features/station/StationMessages'));
const StationSupport = lazy(() => import('./features/station/StationSupport'));
const StationChat = lazy(() => import('./features/station/StationChat'));



const AdminStatusMonitoring = lazy(() => import('./features/admin/AdminStatusMonitoring'));
const AdminInstructorApproval = lazy(() => import('./features/admin/AdminInstructorApproval'));
const AdminLlmManagement = lazy(() => import('./features/admin/AdminLlmManagement'));
const AdminUsageCost = lazy(() => import('./features/admin/AdminUsageCost'));
const AdminUserManagement = lazy(() => import('./features/admin/AdminUserManagement'));
const AdminUserActivity = lazy(() => import('./features/admin/AdminUserActivity'));
const AdminNoticeManagement = lazy(() => import('./features/admin/AdminNoticeManagement'));
const AdminMcpToolsManagement = lazy(() => import('./features/admin/AdminMcpToolsManagement'));
const AdminDataPipeline = lazy(() => import('./features/admin/AdminDataPipeline'));
const AdminClassManagement = lazy(() => import('./features/admin/AdminClassManagement'));


/* 라우트 전환 시 스크롤을 맨 위로 — SPA 기본은 스크롤 위치를 유지하므로,
   랜딩(window 스크롤 사용) 하단에서 이동하면 목적지가 내려간 채 보이는 문제를 막는다. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    const cleanup = listenSystemThemeChange();
    return cleanup;
  }, []);

  return (
    <ErrorBoundary>
    <ScrollToTop />
    <Suspense fallback={null}>
      <Routes>

        <Route path="/" element={<LandingHome />} />
        <Route path="/work-mock-example" element={<WorkMockExample />} />
        <Route path="/notice" element={<LandingNotice />} />
        <Route path="/notices/:id" element={<LandingNotice />} />
        <Route path="/pricing" element={<LandingPrice />} />
        <Route path="/classes" element={<LandingClasses />} />
        <Route path="/support" element={<LandingSupport />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/register" element={<UserSignup />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* OAuth 콜백 (팝업/리다이렉트 수신, 인증 불필요) */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />

        {/* 공개 에이전트 공유 채팅 (인증 불필요) */}
        <Route path="/agent/:token" element={<AgentSharePage />} />

        {/* 신진엠텍 CBL 팀별 에이전트 소개 (공개, 인증 불필요) */}
        <Route path="/shinjin/team1" element={<ShinjinTeamAgent teamId="team1" />} />
        <Route path="/shinjin/team2" element={<ShinjinTeamAgent teamId="team2" />} />

        {/* 신진엠텍 CBL 회차별(①~⑥) 강의안 — 참석 명단 · 진행 내용 (공개, 인증 불필요) */}
        <Route path="/shinjin/day1" element={<ShinjinDayDoc dayId="day1" />} />
        <Route path="/shinjin/day2" element={<ShinjinDayDoc dayId="day2" />} />
        <Route path="/shinjin/day3" element={<ShinjinDayDoc dayId="day3" />} />
        <Route path="/shinjin/day4" element={<ShinjinDayDoc dayId="day4" />} />
        <Route path="/shinjin/day5" element={<ShinjinDayDoc dayId="day5" />} />
        <Route path="/shinjin/day6" element={<ShinjinDayDoc dayId="day6" />} />

        {/* 신진엠텍 CBL 회차별 실습 결과 — 제출물 목록 → 행 클릭 시 상세 서랍 · ?view=all 전체 현황 (공개, MOCK) */}
        <Route path="/shinjin/day1/result" element={<ShinjinDayResult dayId="day1" />} />
        <Route path="/shinjin/day2/result" element={<ShinjinDayResult dayId="day2" />} />
        <Route path="/shinjin/day3/result" element={<ShinjinDayResult dayId="day3" />} />
        <Route path="/shinjin/day4/result" element={<ShinjinDayResult dayId="day4" />} />
        <Route path="/shinjin/day5/result" element={<ShinjinDayResult dayId="day5" />} />
        <Route path="/shinjin/day6/result" element={<ShinjinDayResult dayId="day6" />} />

        {/* 신진엠텍 CBL 회차별 실습 환경 — 이름만 입력해 들어오는 GrowFit 동일 플로우 mock (공개, localStorage) */}
        <Route path="/shinjin/day1/practice/*" element={<ShinjinLabShell dayId="day1" />} />
        <Route path="/shinjin/day2/practice/*" element={<ShinjinLabShell dayId="day2" />} />
        <Route path="/shinjin/day3/practice/*" element={<ShinjinLabShell dayId="day3" />} />
        <Route path="/shinjin/day4/practice/*" element={<ShinjinLabShell dayId="day4" />} />
        <Route path="/shinjin/day5/practice/*" element={<ShinjinLabShell dayId="day5" />} />
        <Route path="/shinjin/day6/practice/*" element={<ShinjinLabShell dayId="day6" />} />



        {/* 신규 디자인 컴포넌트 (features/) */}
        <Route path="/user/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        {/* /user/practice 와 /user/practice/chat/:chatId 는 같은 LlmChat 컴포넌트.
            별도 Route로 두 번 정의하면 navigate 시 unmount/remount → WS effect cleanup → 스트리밍 끊김.
            wildcard로 한 번만 정의하여 LlmChat이 한 번만 마운트되게 한다. chatId는 LlmChat 내부에서 useMatch로 추출. */}
        <Route path="/user/practice/*" element={<ProtectedRoute><LlmChat /></ProtectedRoute>} />
        <Route path="/prompt" element={<ProtectedRoute><PromptIntro /></ProtectedRoute>} />
        <Route path="/prompt/generate" element={<ProtectedRoute><PromptGenerator /></ProtectedRoute>} />
        <Route path="/prompt/list" element={<ProtectedRoute><PromptList type="prompt" /></ProtectedRoute>} />
        <Route path="/prompt/list/:id/edit" element={<ProtectedRoute><PromptEdit type="prompt" /></ProtectedRoute>} />
        <Route path="/prompt/few-shot" element={<ProtectedRoute><PromptList type="few-shot" /></ProtectedRoute>} />
        <Route path="/prompt/few-shot/:id/edit" element={<ProtectedRoute><PromptEdit type="few-shot" /></ProtectedRoute>} />
        <Route path="/knowledge" element={<Navigate to="/knowledge/list" replace />} />
        <Route path="/knowledge/list" element={<ProtectedRoute><KnowledgeList /></ProtectedRoute>} />
        <Route path="/mcp" element={<ProtectedRoute><McpIntro /></ProtectedRoute>} />
        <Route path="/mcp/list" element={<ProtectedRoute><McpList /></ProtectedRoute>} />
        <Route path="/mcp/generate" element={<ProtectedRoute><McpGenerator /></ProtectedRoute>} />
        <Route path="/mcp/builder" element={<ProtectedRoute><McpBuilder /></ProtectedRoute>} />
        <Route path="/agent-builder" element={<ProtectedRoute><AgentList /></ProtectedRoute>} />
        <Route path="/agent-builder/new" element={<ProtectedRoute><AgentCreate /></ProtectedRoute>} />
        <Route path="/agent-builder/integrations" element={<ProtectedRoute><AgentIntegrations /></ProtectedRoute>} />
        <Route path="/agent-builder/skills" element={<ProtectedRoute><AgentSkills /></ProtectedRoute>} />
        <Route path="/agent-builder/showcase" element={<ProtectedRoute><AgentShowcase /></ProtectedRoute>} />
        <Route path="/agent-builder/showcase/:agentId" element={<ProtectedRoute><AgentShowcaseDetail /></ProtectedRoute>} />
        <Route path="/agent-builder/showcase/:agentId/work" element={<ProtectedRoute><AgentShowcaseWork /></ProtectedRoute>} />
        <Route path="/agent-builder/:agentId" element={<ProtectedRoute><AgentChat /></ProtectedRoute>} />
        <Route path="/edu" element={<ProtectedRoute><EduList /></ProtectedRoute>} />
        <Route path="/edu/course/:courseId" element={<ProtectedRoute><EduCourse /></ProtectedRoute>} />
        <Route path="/edu/course/:courseId/detail" element={<ProtectedRoute><EduCourseSteps /></ProtectedRoute>} />
        <Route path="/edu/my" element={<ProtectedRoute><EduMy /></ProtectedRoute>} />
        <Route path="/edu/enrolled" element={<ProtectedRoute><EduEnrolled /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><TemplateList /></ProtectedRoute>} />
        <Route path="/project" element={<ProtectedRoute><ProjectList /></ProtectedRoute>} />
        <Route path="/project/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
        <Route path="/settings/account" element={<ProtectedRoute><SettingsAccount /></ProtectedRoute>} />
        <Route path="/settings/security" element={<ProtectedRoute><SettingsSecurity /></ProtectedRoute>} />
        <Route path="/settings/usage" element={<ProtectedRoute><SettingsUsage /></ProtectedRoute>} />
        <Route path="/settings/billing" element={<ProtectedRoute><SettingsBilling /></ProtectedRoute>} />
        <Route path="/settings/notifications" element={<ProtectedRoute><SettingsNotifications /></ProtectedRoute>} />
        <Route path="/settings/preferences" element={<ProtectedRoute><SettingsPreferences /></ProtectedRoute>} />
        <Route path="/settings/connections" element={<ProtectedRoute><SettingsConnections /></ProtectedRoute>} />
        <Route path="/settings/support" element={<ProtectedRoute><SettingsSupport /></ProtectedRoute>} />
        <Route path="/settings/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
        <Route path="/settings/billing/fail" element={<ProtectedRoute><BillingFail /></ProtectedRoute>} />



        {/* 그로우핏 스테이션 */}
        <Route path="/station" element={<Navigate to="/station/agents" replace />} />
        <Route path="/station/agents" element={<ProtectedRoute><StationAgents /></ProtectedRoute>} />
        <Route path="/station/statistics" element={<ProtectedRoute><StationStatistics /></ProtectedRoute>} />
        <Route path="/station/favorites" element={<ProtectedRoute><StationFavorites /></ProtectedRoute>} />
        <Route path="/station/messages" element={<ProtectedRoute><StationMessages /></ProtectedRoute>} />
        <Route path="/station/support" element={<ProtectedRoute><StationSupport /></ProtectedRoute>} />
        <Route path="/station/chat/:agentId" element={<ProtectedRoute><StationChat /></ProtectedRoute>} />


        {/* 플랫폼 관리자 컴포넌트 */}
        <Route path="/gf-admin" element={<AdminLogin />} />
        <Route path="/gf-admin/monitoring" element={<AdminRoute><AdminStatusMonitoring /></AdminRoute>} />
        <Route path="/gf-admin/instructor" element={<AdminRoute><AdminInstructorApproval /></AdminRoute>} />
        <Route path="/gf-admin/llm" element={<AdminRoute><AdminLlmManagement /></AdminRoute>} />
        <Route path="/gf-admin/usage" element={<AdminRoute><AdminUsageCost /></AdminRoute>} />
        <Route path="/gf-admin/users" element={<AdminRoute><AdminUserManagement /></AdminRoute>} />
        <Route path="/gf-admin/users/:userId/activity" element={<AdminRoute><AdminUserActivity /></AdminRoute>} />
        <Route path="/gf-admin/notices" element={<AdminRoute><AdminNoticeManagement /></AdminRoute>} />
        <Route path="/gf-admin/mcp-tools" element={<AdminRoute><AdminMcpToolsManagement /></AdminRoute>} />
        <Route path="/gf-admin/data-pipeline" element={<AdminRoute><AdminDataPipeline /></AdminRoute>} />
        <Route path="/gf-admin/classes" element={<AdminRoute><AdminClassManagement /></AdminRoute>} />


        {/* 404 */}
        <Route path="*" element={<NotFound />} />

      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
